import { useEffect, useState } from "react";
import { supabase } from "@vinoga/db";
import { UserPlus, Save, Trash2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useT } from "@/lib/i18n";
import { api, ApiError } from "@/lib/api";

type AdminUser = {
  id: string;
  email: string;
  full_name: string | null;
  role: "admin" | "staff";
  active: boolean;
  last_login_at: string | null;
  created_at: string;
};

export function Team() {
  const { toast } = useToast();
  const t = useT();

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState<"admin" | "staff">("staff");
  const [newPassword, setNewPassword] = useState("");
  const [creating, setCreating] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("admin_users")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      toast({
        title: t("team.errorLoading", "Error loading team"),
        description: error.message,
        variant: "destructive",
      });
      setLoading(false);
      return;
    }
    setUsers((data ?? []) as AdminUser[]);
    setLoading(false);
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateUserField = (id: string, patch: Partial<AdminUser>) => {
    setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, ...patch } : u)));
  };

  const saveUser = async (user: AdminUser) => {
    setSavingId(user.id);
    const { error } = await supabase
      .from("admin_users")
      .update({
        full_name: user.full_name,
        role: user.role,
        active: user.active,
      })
      .eq("id", user.id);
    if (error) {
      toast({
        title: t("common.saveFailed", "Save failed"),
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({ title: t("team.saved", "Team member saved") });
    }
    setSavingId(null);
  };

  const deleteUser = async () => {
    if (!deleteId) return;
    const id = deleteId;
    setDeleteId(null);
    const { error } = await supabase.from("admin_users").delete().eq("id", id);
    if (error) {
      toast({
        title: t("common.deleteFailed", "Delete failed"),
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    toast({ title: t("team.deleted", "Team member removed") });
    fetchUsers();
  };

  const createUser = async () => {
    if (!newEmail.trim() || !newPassword.trim()) return;
    if (newPassword.length < 8) {
      toast({
        title: t("team.passwordTooShort", "Password too short"),
        description: t(
          "team.passwordMinLength",
          "Password must be at least 8 characters"
        ),
        variant: "destructive",
      });
      return;
    }
    setCreating(true);
    try {
      await api("/api/v1/admin/team", {
        method: "POST",
        body: JSON.stringify({
          email: newEmail.trim().toLowerCase(),
          full_name: newName.trim() || null,
          role: newRole,
          password: newPassword,
        }),
      });
    } catch (err) {
      const msg =
        err instanceof ApiError && err.code === "email_in_use"
          ? t("team.emailInUse", "That email is already on the team")
          : (err as Error).message;
      toast({
        title: t("team.createFailed", "Failed to add team member"),
        description: msg,
        variant: "destructive",
      });
      setCreating(false);
      return;
    }
    toast({
      title: t("team.created", "Team member added"),
    });
    setCreateOpen(false);
    setNewEmail("");
    setNewName("");
    setNewPassword("");
    setNewRole("staff");
    setCreating(false);
    fetchUsers();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-xl font-bold text-foreground">
            {t("team.title", "Team")}
          </h1>
          <p className="font-body text-xs text-muted-foreground">
            {t(
              "team.subtitle",
              "Admin and staff users with access to this panel."
            )}
          </p>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <UserPlus size={14} /> {t("team.invite", "Add member")}
        </Button>
      </div>

      {loading ? (
        <div className="p-4 font-body text-sm text-muted-foreground">
          {t("common.loading", "Loading...")}
        </div>
      ) : users.length === 0 ? (
        <p className="font-body text-sm text-muted-foreground">
          {t("team.empty", "No team members yet.")}
        </p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-bold">{t("team.colEmail", "Email")}</TableHead>
                <TableHead className="font-bold">{t("team.colName", "Name")}</TableHead>
                <TableHead className="font-bold">{t("team.colRole", "Role")}</TableHead>
                <TableHead className="text-center font-bold">
                  {t("team.colActive", "Active")}
                </TableHead>
                <TableHead className="font-bold">
                  {t("team.colLastLogin", "Last login")}
                </TableHead>
                <TableHead className="text-right font-bold">
                  {t("team.colActions", "Actions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id}>
                  <TableCell className="py-2.5">
                    <div className="flex items-center gap-2">
                      <ShieldCheck
                        size={14}
                        className={
                          u.role === "admin" ? "text-primary" : "text-muted-foreground/40"
                        }
                      />
                      <span className="font-medium">{u.email}</span>
                    </div>
                  </TableCell>
                  <TableCell className="py-2.5">
                    <Input
                      value={u.full_name ?? ""}
                      onChange={(e) =>
                        updateUserField(u.id, { full_name: e.target.value || null })
                      }
                      placeholder={t("team.namePlaceholder", "Display name")}
                      className="h-8 font-body text-sm"
                    />
                  </TableCell>
                  <TableCell className="py-2.5">
                    <Select
                      value={u.role}
                      onValueChange={(val) =>
                        updateUserField(u.id, { role: val as "admin" | "staff" })
                      }
                    >
                      <SelectTrigger className="h-8 w-[110px] font-body text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">{t("team.roleAdmin", "Admin")}</SelectItem>
                        <SelectItem value="staff">{t("team.roleStaff", "Staff")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="py-2.5 text-center">
                    <Badge variant={u.active ? "success" : "secondary"}>
                      {u.active
                        ? t("team.statusActive", "Active")
                        : t("team.statusDisabled", "Disabled")}
                    </Badge>
                  </TableCell>
                  <TableCell className="py-2.5 text-xs text-muted-foreground">
                    {u.last_login_at
                      ? new Date(u.last_login_at).toLocaleDateString()
                      : "—"}
                  </TableCell>
                  <TableCell className="py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2"
                        onClick={() =>
                          updateUserField(u.id, { active: !u.active })
                        }
                      >
                        {u.active
                          ? t("team.disable", "Disable")
                          : t("team.enable", "Enable")}
                      </Button>
                      <Button
                        size="sm"
                        className="h-7 px-2"
                        onClick={() => saveUser(u)}
                        disabled={savingId === u.id}
                      >
                        <Save size={12} />
                        {savingId === u.id
                          ? t("common.saving", "Saving…")
                          : t("common.save", "Save")}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 hover:text-destructive"
                        onClick={() => setDeleteId(u.id)}
                      >
                        <Trash2 size={14} className="text-muted-foreground" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">
              {t("team.invite", "Add team member")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="font-body text-xs text-muted-foreground">
                {t("team.colEmail", "Email")} *
              </label>
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
            </div>
            <div>
              <label className="font-body text-xs text-muted-foreground">
                {t("team.colName", "Display name")}
              </label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} />
            </div>
            <div>
              <label className="font-body text-xs text-muted-foreground">
                {t("team.colRole", "Role")}
              </label>
              <Select
                value={newRole}
                onValueChange={(val) => setNewRole(val as "admin" | "staff")}
              >
                <SelectTrigger className="font-body text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">{t("team.roleAdmin", "Admin")}</SelectItem>
                  <SelectItem value="staff">{t("team.roleStaff", "Staff")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="font-body text-xs text-muted-foreground">
                {t("team.tempPassword", "Temporary password")} *
              </label>
              <Input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t("team.tempPasswordPlaceholder", "Min. 8 characters")}
              />
              <p className="mt-1 font-body text-[11px] italic text-muted-foreground">
                {t(
                  "team.tempPasswordHint",
                  "Until the invite-email endpoint ships, the user receives this password manually. They can change it on first login."
                )}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              onClick={createUser}
              disabled={creating || !newEmail.trim() || !newPassword.trim()}
            >
              {creating ? t("common.creating", "Creating…") : t("common.create", "Create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("team.confirmDeleteTitle", "Remove team member?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "team.confirmDeleteDesc",
                "This permanently removes the user's access. Audit-log entries they created remain."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel", "Cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={deleteUser}>
              {t("common.delete", "Delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
